/**
 * Browser-style deck frame + column mocks (matches DeckMockup visual language).
 * Used on category pages to showcase the first recipe template’s column layout.
 */

import React, { useMemo } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Pressable,
  Platform,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { BORDER_RADIUS } from '@/constants/config';
import { getColumnMeta, getColumnBindLabel, getColumnSubtitle } from '@/utils/deckRecipeCategory';

const isWeb = Platform.OS === 'web';

const MONO = Platform.select({
  ios: 'Menlo',
  android: 'monospace',
  default: '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, Consolas, monospace',
});

const PREVIEW_COL_W = 208;
const PREVIEW_COL_H = 268;

function modalityToMockKey(modality) {
  const m = String(modality || 'tool').toLowerCase();
  const map = {
    llm: 'chat',
    chat: 'chat',
    conversation: 'chat',
    image: 'image',
    model: 'image',
    video: 'video',
    music: 'music',
    voice: 'voice',
    transcribe: 'transcribe',
    avatar: 'avatar',
    upscale: 'upscale',
    tool: 'tool',
    agent: 'tool',
    sfx: 'music',
    '3d': '3d',
    threed: '3d',
    object: '3d',
  };
  return map[m] || 'tool';
}

// Resolve a human-readable label for the bound model/tool/agent. Reads both
// the nested seed shape (`column.bind?.modelSlug`) and the flattened DB shape
// (`column.bindModelSlug`) so previews work whether the data is hydrated
// from JSON or from a Mongo document.
function bindLabel(col) {
  return getColumnBindLabel(col);
}

function MockChatColumn({ tint }) {
  const { colors } = useTheme();
  return (
    <View style={s.colInner}>
      <View style={[s.userBubble, { backgroundColor: colors.solid_color_hover }]}>
        <View style={[s.bubbleLine, { backgroundColor: colors.text_color_tertiary, width: '70%' }]} />
        <View style={[s.bubbleLine, { backgroundColor: colors.text_color_tertiary, width: '40%' }]} />
      </View>
      <View style={s.assistantBlock}>
        {[0.9, 0.85, 0.75, 0.6].map((w, i) => (
          <View key={i} style={[s.line, { backgroundColor: colors.text_color_tertiary, width: `${w * 100}%`, opacity: 0.55 }]} />
        ))}
      </View>
      <View style={[s.streamingDot, { backgroundColor: tint }]} />
    </View>
  );
}

function MockImageColumn() {
  const { colors } = useTheme();
  const grads = [
    ['#A855F7', '#3B82F6'],
    ['#F472B6', '#A855F7'],
    ['#10B981', '#06B6D4'],
    ['#F59E0B', '#EF4444'],
  ];
  return (
    <View style={s.imgGrid}>
      {grads.map((g, i) => (
        <View key={i} style={[s.imgTile, { borderColor: colors.border_color_secondary }]}>
          {isWeb ? (
            <View style={[StyleSheet.absoluteFill, { backgroundImage: `linear-gradient(135deg, ${g[0]}, ${g[1]})` }]} />
          ) : (
            <LinearGradient colors={g} style={StyleSheet.absoluteFill} />
          )}
        </View>
      ))}
    </View>
  );
}

function MockVideoColumn() {
  const { colors } = useTheme();
  return (
    <View style={s.videoWrap}>
      <View style={[s.videoThumb, { borderColor: colors.border_color_secondary }]}>
        {isWeb ? (
          <View style={[StyleSheet.absoluteFill, { backgroundImage: 'linear-gradient(160deg, #1e293b 0%, #0f172a 70%, #312e81 100%)' }]} />
        ) : (
          <LinearGradient colors={['#1e293b', '#0f172a', '#312e81']} style={StyleSheet.absoluteFill} />
        )}
        <View style={s.playBtn}>
          <Icon name="play" size={14} color="#fff" solid />
        </View>
        <View style={s.videoTimebar}>
          <View style={[s.videoTimebarFill, { backgroundColor: '#fff' }]} />
        </View>
      </View>
      <View style={s.videoMeta}>
        <Text size="xxs" color="tertiary" weight="medium">00:08 · 1080p</Text>
      </View>
    </View>
  );
}

function MockMusicColumn() {
  const { colors } = useTheme();
  const bars = 24;
  return (
    <View style={s.musicWrap}>
      <View style={s.waveform}>
        {[...Array(bars)].map((_, i) => {
          const h = 5 + Math.abs(Math.sin(i * 0.6)) * 26 + Math.abs(Math.cos(i * 1.3)) * 12;
          return (
            <View key={i} style={[s.bar, { height: h, backgroundColor: i < 14 ? '#10B981' : colors.border_color_primary }]} />
          );
        })}
      </View>
      <View style={s.musicMeta}>
        <Icon name="music" size={9} color={colors.text_color_tertiary} solid />
        <Text size="xxs" color="tertiary" weight="medium">2:14 · ambient</Text>
      </View>
    </View>
  );
}

function MockTranscribeColumn() {
  const { colors } = useTheme();
  const lines = [
    { t: '00:02', w: '85%' },
    { t: '00:09', w: '70%' },
    { t: '00:14', w: '92%' },
    { t: '00:21', w: '60%' },
  ];
  return (
    <View style={s.transcribeWrap}>
      {lines.map((l, i) => (
        <View key={i} style={s.transcribeRow}>
          <Text size="xxs" weight="bold" style={{ color: '#06B6D4', width: 34, fontFamily: MONO }}>{l.t}</Text>
          <View style={{ flex: 1, marginTop: 5 }}>
            <View style={[s.line, { backgroundColor: colors.text_color_tertiary, width: l.w, opacity: 0.55, height: 4 }]} />
          </View>
        </View>
      ))}
    </View>
  );
}

function MockAvatarColumn() {
  return (
    <View style={s.avatarWrap}>
      <View style={s.avatarStage}>
        {isWeb ? (
          <View style={[StyleSheet.absoluteFill, { backgroundImage: 'radial-gradient(ellipse at 50% 35%, #fbbf2422 0%, transparent 60%), linear-gradient(180deg, #1c1917 0%, #0c0a09 100%)' }]} />
        ) : (
          <LinearGradient colors={['#1c1917', '#0c0a09']} style={StyleSheet.absoluteFill} />
        )}
        <View style={[s.avatarHead, { backgroundColor: '#8B5CF6' }]}>
          <Icon name="user-tie" size={16} color="#fff" solid />
        </View>
        <View style={[s.recDot, { backgroundColor: '#ef4444' }]} />
      </View>
      <View style={s.musicMeta}>
        <View style={[s.recDotMini, { backgroundColor: '#ef4444' }]} />
        <Text size="xxs" color="tertiary" weight="medium">Speaking</Text>
      </View>
    </View>
  );
}

function MockVoiceColumn({ tint }) {
  const { colors } = useTheme();
  return (
    <View style={s.voiceWrap}>
      <View style={[s.voiceStage, { borderColor: colors.border_color_secondary }]}>
        {isWeb ? (
          <View style={[StyleSheet.absoluteFill, { backgroundImage: `radial-gradient(circle at 50% 40%, ${tint}33 0%, transparent 65%)` }]} />
        ) : (
          <LinearGradient colors={[`${tint}33`, 'transparent']} style={StyleSheet.absoluteFill} />
        )}
        <View style={[s.voiceMic, { backgroundColor: tint }]}>
          <Icon name="microphone" size={14} color="#fff" solid />
        </View>
        <View style={s.voiceWave}>
          {[...Array(18)].map((_, i) => {
            const h = 4 + Math.abs(Math.sin(i * 0.55)) * 18 + Math.abs(Math.cos(i * 0.95)) * 8;
            return <View key={i} style={{ width: 2, height: h, borderRadius: 1, backgroundColor: tint, opacity: i < 12 ? 0.85 : 0.35 }} />;
          })}
        </View>
      </View>
      <View style={s.musicMeta}>
        <View style={[s.recDotMini, { backgroundColor: tint }]} />
        <Text size="xxs" color="tertiary" weight="medium">0:42 · TTS</Text>
      </View>
    </View>
  );
}

function MockUpscaleColumn({ tint }) {
  const { colors } = useTheme();
  return (
    <View style={s.upscaleWrap}>
      <View style={s.upscaleRow}>
        <View style={[s.upscaleSmall, { borderColor: colors.border_color_secondary }]}>
          {isWeb ? (
            <View style={[StyleSheet.absoluteFill, { backgroundImage: 'linear-gradient(135deg, #A855F7, #3B82F6)', filter: 'blur(2px)' }]} />
          ) : (
            <LinearGradient colors={['#A855F7', '#3B82F6']} style={StyleSheet.absoluteFill} />
          )}
          <View style={[s.upscaleLabel, { backgroundColor: 'rgba(0,0,0,0.55)' }]}>
            <Text style={{ fontFamily: MONO, fontSize: 7, color: '#fff', letterSpacing: 0.3 }}>1×</Text>
          </View>
        </View>
        <View style={s.upscaleArrow}>
          <Icon name="arrow-right" size={11} color={tint} solid />
        </View>
        <View style={[s.upscaleLarge, { borderColor: tint, ...(isWeb ? { boxShadow: `0 0 0 2px ${tint}33` } : {}) }]}>
          {isWeb ? (
            <View style={[StyleSheet.absoluteFill, { backgroundImage: 'linear-gradient(135deg, #A855F7, #3B82F6)' }]} />
          ) : (
            <LinearGradient colors={['#A855F7', '#3B82F6']} style={StyleSheet.absoluteFill} />
          )}
          <View style={[s.upscaleLabel, { backgroundColor: tint }]}>
            <Text style={{ fontFamily: MONO, fontSize: 7, color: '#fff', letterSpacing: 0.3 }}>4×</Text>
          </View>
        </View>
      </View>
      <View style={[s.upscaleProgress, { backgroundColor: `${tint}1f` }]}>
        <View style={{ height: 3, width: '72%', backgroundColor: tint, borderRadius: 1.5 }} />
      </View>
      <View style={s.musicMeta}>
        <View style={[s.recDotMini, { backgroundColor: tint }]} />
        <Text size="xxs" color="tertiary" weight="medium">512 → 2048</Text>
      </View>
    </View>
  );
}

function Mock3DColumn({ tint }) {
  const { colors } = useTheme();
  return (
    <View style={s.threeDWrap}>
      <View style={[s.threeDStage, { borderColor: colors.border_color_secondary }]}>
        {isWeb ? (
          <>
            <View style={[StyleSheet.absoluteFill, { backgroundImage: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)' }]} />
            <View style={[StyleSheet.absoluteFill, { backgroundImage: `radial-gradient(circle at 50% 42%, ${tint}40 0%, transparent 60%)` }]} />
            <View
              pointerEvents="none"
              style={{
                position: 'absolute',
                left: -34,
                right: -34,
                top: '58%',
                bottom: -8,
                backgroundImage: `linear-gradient(${tint}26 1px, transparent 1px), linear-gradient(90deg, ${tint}26 1px, transparent 1px)`,
                backgroundSize: '12px 12px',
                opacity: 0.7,
                transform: 'perspective(120px) rotateX(58deg)',
                transformOrigin: 'center top',
              }}
            />
          </>
        ) : (
          <LinearGradient colors={['#0f172a', '#020617']} style={StyleSheet.absoluteFill} />
        )}
        <View style={[s.threeDCube, { backgroundColor: tint, ...(isWeb ? { boxShadow: `0 5px 22px ${tint}55, 0 0 0 4px ${tint}1f` } : {}) }]}>
          <Icon name="cubes-stacked" size={18} color="#fff" solid />
        </View>
        <View style={[s.threeDPill, { backgroundColor: 'rgba(255,255,255,0.14)', borderColor: 'rgba(255,255,255,0.18)' }]}>
          <Text style={{ fontFamily: MONO, fontSize: 7, color: '#fff', letterSpacing: 0.4 }}>GLB</Text>
        </View>
      </View>
      <View style={s.musicMeta}>
        <View style={[s.recDotMini, { backgroundColor: tint }]} />
        <Text size="xxs" color="tertiary" weight="medium">12k tris · PBR</Text>
      </View>
    </View>
  );
}

function MockToolColumn({ tint }) {
  const { colors } = useTheme();
  return (
    <View style={s.toolWrap}>
      <View style={[s.toolInputRow, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}>
        <Icon name="image" size={9} color={colors.text_color_tertiary} solid />
        <Text size="xxs" color="tertiary" weight="medium" numberOfLines={1} style={{ flex: 1 }}>input.png</Text>
      </View>
      <View style={s.toolArrow}>
        <Icon name="arrow-down" size={9} color={colors.text_color_tertiary} />
      </View>
      <View style={[s.toolStage, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}>
        {isWeb ? (
          <View style={[StyleSheet.absoluteFill, { backgroundImage: `radial-gradient(circle at 50% 40%, ${tint}33 0%, transparent 65%)` }]} />
        ) : (
          <LinearGradient colors={[`${tint}33`, 'transparent']} style={StyleSheet.absoluteFill} />
        )}
        <View style={[s.toolBadge, { backgroundColor: tint }]}>
          <Icon name="wand-magic-sparkles" size={12} color="#fff" solid />
        </View>
        <View style={s.toolProgressTrack}>
          <View style={[s.toolProgressFill, { backgroundColor: tint, width: '62%' }]} />
        </View>
      </View>
      <View style={s.musicMeta}>
        <View style={[s.recDotMini, { backgroundColor: tint }]} />
        <Text size="xxs" color="tertiary" weight="medium">Running…</Text>
      </View>
    </View>
  );
}

const MOCK_COMPONENTS = {
  chat: MockChatColumn,
  image: MockImageColumn,
  video: MockVideoColumn,
  music: MockMusicColumn,
  voice: MockVoiceColumn,
  transcribe: MockTranscribeColumn,
  avatar: MockAvatarColumn,
  upscale: MockUpscaleColumn,
  tool: MockToolColumn,
  '3d': Mock3DColumn,
};

function ColumnScroll({ children }) {
  if (isWeb) {
    return (
      <div
        className="deck-template-preview-scroll"
        style={{
          display: 'flex',
          flexDirection: 'row',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <style>{`.deck-template-preview-scroll::-webkit-scrollbar { display: none; }`}</style>
        {children}
      </div>
    );
  }
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.cols}>
      {children}
    </ScrollView>
  );
}

const DEFAULT_FOOTER_EYEBROW = 'Example layout';
const DEFAULT_FOOTER_DESCRIPTION = 'First recipe in this category — open it to clone this column stack.';

/**
 * @param {object} props
 * @param {object} props.template — enriched deck template (`columns[]`, `name`, `slug`)
 * @param {boolean} [props.isXS]
 * @param {() => void} [props.onPress] — entire frame (e.g. open first recipe)
 * @param {string} [props.footerEyebrow] — caption line above the recipe name (default: category copy)
 * @param {string} [props.footerDescription] — caption line under the recipe name
 */
export default function DeckTemplateFramePreview({
  template,
  isXS,
  onPress,
  footerEyebrow = DEFAULT_FOOTER_EYEBROW,
  footerDescription = DEFAULT_FOOTER_DESCRIPTION,
}) {
  const { colors } = useTheme();
  const colW = isXS ? Math.min(PREVIEW_COL_W, 168) : PREVIEW_COL_W;
  const colH = isXS ? 240 : PREVIEW_COL_H;

  const columns = useMemo(() => {
    const raw = Array.isArray(template?.columns) ? template.columns : [];
    return raw.map((col, i) => {
      const meta = getColumnMeta(col);
      const key = modalityToMockKey(col.modality || col.type);
      const Component = MOCK_COMPONENTS[key] || MockToolColumn;
      const bound = bindLabel(col);
      // Tiny mono caption under the column title: prefer the bound model /
      // tool / agent name (snappy + truncates well in the cramped preview),
      // fall back to the editorial subtitle, then to the modality label.
      const caption = bound || getColumnSubtitle(col) || meta.label;
      const step = col.label || meta.label;
      let extra = {};
      if (key === 'chat') extra = { tint: meta.color };
      else if (key === 'voice' || key === 'upscale' || key === 'tool' || key === '3d') extra = { tint: meta.color };
      return {
        i, meta, Component, extra, title: caption, step, key,
      };
    });
  }, [template]);

  const n = columns.length;
  const chromeLabel = template?.name || 'Deck preview';
  const countLabel = `${n} / 12`;

  const inner = (
    <View style={s.frameWrap}>
      {isWeb && (
        <View
          pointerEvents="none"
          style={{
            position: 'absolute',
            top: 22,
            left: 22,
            right: 22,
            bottom: -14,
            borderRadius: BORDER_RADIUS.large,
            backgroundColor: '#3b82f6',
            opacity: 0.09,
            filter: 'blur(44px)',
          }}
        />
      )}
      <View style={[s.frame, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
        <View style={[s.chrome, { borderBottomColor: colors.border_color_secondary }]}>
          <View style={s.dots}>
            <View style={[s.dot, { backgroundColor: '#ff5f57' }]} />
            <View style={[s.dot, { backgroundColor: '#febc2e' }]} />
            <View style={[s.dot, { backgroundColor: '#28c840' }]} />
          </View>
          <View style={[s.urlPill, { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary }]}>
            <Icon name="lock" size={9} color={colors.text_color_tertiary} solid />
            <Text size="xxs" color="tertiary" weight="medium" numberOfLines={1} style={{ flex: 1, minWidth: 0 }}>
              gab.ai / deck — {template?.slug || 'recipe'}
            </Text>
          </View>
          <View style={s.chromeRight}>
            <Text size="xxs" color="tertiary" weight="medium" style={{ fontFamily: MONO }}>{countLabel}</Text>
          </View>
        </View>

        <ColumnScroll>
          {columns.map((c) => (
            <View
              key={c.i}
              style={[
                s.col,
                {
                  width: colW,
                  height: colH,
                  borderRightColor: colors.border_color_secondary,
                  borderRightWidth: 1,
                },
              ]}
            >
              <View style={[s.colHeader, { borderBottomColor: colors.border_color_secondary }]}>
                <View style={[s.colTypeBadge, { backgroundColor: `${c.meta.color}1f` }]}>
                  <Icon name={c.meta.icon} size={10} color={c.meta.color} solid />
                </View>
                <View style={{ flex: 1, gap: 1, minWidth: 0 }}>
                  <Text size="xxs" weight="bold" color="primary" numberOfLines={1}>{c.step}</Text>
                  <Text color='tertiary' style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 0.2 }} numberOfLines={1}>
                    {c.title}
                  </Text>
                </View>
              </View>
              <View style={s.colBody}>
                <c.Component {...c.extra} />
              </View>
            </View>
          ))}
          <View
            style={[
              s.col,
              s.addCol,
              {
                width: colW,
                height: colH,
                borderColor: colors.border_color_secondary,
              },
            ]}
          >
            <View style={[s.addColIcon, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}>
              <Icon name="plus" size={15} color={colors.text_color_tertiary} solid />
            </View>
            <Text size="xxs" weight="bold" color="secondary" style={{ marginTop: 8 }}>
              Add a column
            </Text>
            <Text size="xxs" color="tertiary" style={{ textAlign: 'center', marginTop: 4, lineHeight: 14, paddingHorizontal: 14 }}>
              100+ models & tools
            </Text>
          </View>
        </ColumnScroll>
      </View>

      <View style={s.captionRow}>
        <Text size="xxs" weight="bold" color="tertiary" style={{ letterSpacing: 1.1, textTransform: 'uppercase' }}>
          {footerEyebrow}
        </Text>
        <Text size="xs" weight="bold" color="primary" style={{ marginTop: 6, letterSpacing: -0.2 }} numberOfLines={2}>
          {chromeLabel}
        </Text>
        <Text size="xxs" color="secondary" style={{ marginTop: 4, lineHeight: 18 }} numberOfLines={3}>
          {footerDescription}
        </Text>
      </View>
    </View>
  );

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        style={({ hovered }) => [
          s.pressableOuter,
          s.pressableClickable,
          isWeb && hovered ? { opacity: 0.94 } : null,
        ]}
      >
        {inner}
      </Pressable>
    );
  }

  return <View style={s.pressableOuter}>{inner}</View>;
}

const s = StyleSheet.create({
  pressableOuter: { width: '100%', alignSelf: 'stretch' },
  pressableClickable: isWeb ? { cursor: 'pointer' } : {},
  frameWrap: {
    marginTop: 4,
    marginBottom: 4,
    position: 'relative',
    width: '100%',
  },
  frame: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.large,
    overflow: 'hidden',
    ...(isWeb ? { boxShadow: '0 20px 50px rgba(0,0,0,0.09)' } : {}),
  },
  chrome: {
    height: 36,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    gap: 8,
  },
  dots: { flexDirection: 'row', gap: 5, width: 62, flexShrink: 0 },
  dot: { width: 9, height: 9, borderRadius: 5 },
  urlPill: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    minWidth: 0,
  },
  chromeRight: { width: 56, alignItems: 'flex-end', flexShrink: 0 },

  cols: { flexDirection: 'row' },
  col: { flexShrink: 0 },
  addCol: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'dashed',
  },
  addColIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  colHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingHorizontal: 9,
    paddingVertical: 8,
    borderBottomWidth: 1,
  },
  colTypeBadge: {
    width: 18,
    height: 18,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  colBody: { flex: 1, padding: 9 },

  colInner: { flex: 1, gap: 8, position: 'relative' },
  userBubble: {
    alignSelf: 'flex-end',
    maxWidth: '90%',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 8,
    borderBottomRightRadius: 3,
    gap: 4,
  },
  bubbleLine: { height: 4, borderRadius: 2 },
  assistantBlock: { gap: 4 },
  line: { height: 4, borderRadius: 2 },
  streamingDot: {
    position: 'absolute',
    bottom: 3,
    left: 3,
    width: 5,
    height: 5,
    borderRadius: 3,
  },

  imgGrid: { flex: 1, flexDirection: 'row', flexWrap: 'wrap', gap: 5 },
  imgTile: {
    flexBasis: '47%',
    flexGrow: 1,
    aspectRatio: 1,
    borderRadius: 6,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },

  videoWrap: { flex: 1, gap: 6 },
  videoThumb: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderWidth: 1.5,
    borderColor: '#ffffff66',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoTimebar: {
    position: 'absolute',
    bottom: 5,
    left: 7,
    right: 7,
    height: 2,
    borderRadius: 1,
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  videoTimebarFill: { width: '38%', height: 2, borderRadius: 1 },
  videoMeta: { flexDirection: 'row', alignItems: 'center', gap: 4 },

  musicWrap: { flex: 1, gap: 8, justifyContent: 'space-between' },
  waveform: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 2 },
  bar: { flex: 1, borderRadius: 1.5, minHeight: 4 },
  musicMeta: { flexDirection: 'row', alignItems: 'center', gap: 4 },

  transcribeWrap: { flex: 1, gap: 6 },
  transcribeRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 6 },

  avatarWrap: { flex: 1, gap: 6 },
  avatarStage: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  avatarHead: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb ? { boxShadow: '0 4px 14px rgba(0,0,0,0.35)' } : {}),
  },
  recDot: {
    position: 'absolute',
    top: 7,
    right: 7,
    width: 5,
    height: 5,
    borderRadius: 3,
  },
  recDotMini: { width: 5, height: 5, borderRadius: 3 },

  toolWrap: { flex: 1, gap: 5 },
  toolInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 7,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
  },
  toolArrow: { alignItems: 'center', paddingVertical: 1 },
  toolStage: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    paddingHorizontal: 10,
  },
  toolBadge: {
    width: 34,
    height: 34,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb ? { boxShadow: '0 6px 16px rgba(99,102,241,0.3)' } : {}),
  },
  toolProgressTrack: {
    position: 'absolute',
    bottom: 7,
    left: 9,
    right: 9,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: 'rgba(99,102,241,0.18)',
    overflow: 'hidden',
  },
  toolProgressFill: { height: 3, borderRadius: 1.5 },

  voiceWrap: { flex: 1, gap: 6 },
  voiceStage: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    gap: 10,
    paddingHorizontal: 10,
  },
  voiceMic: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb ? { boxShadow: '0 4px 12px rgba(245,158,11,0.3)' } : {}),
  },
  voiceWave: { flexDirection: 'row', alignItems: 'center', gap: 2, height: 26 },

  upscaleWrap: { flex: 1, gap: 8, justifyContent: 'space-between' },
  upscaleRow: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  upscaleSmall: {
    width: 34,
    height: 34,
    borderRadius: 5,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  upscaleArrow: { paddingHorizontal: 2 },
  upscaleLarge: {
    width: 68,
    height: 68,
    borderRadius: 7,
    borderWidth: 1.5,
    overflow: 'hidden',
    position: 'relative',
  },
  upscaleLabel: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    paddingHorizontal: 3,
    paddingVertical: 1,
    borderRadius: 3,
  },
  upscaleProgress: { height: 3, borderRadius: 1.5, overflow: 'hidden' },

  threeDWrap: { flex: 1, gap: 6 },
  threeDStage: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  threeDCube: {
    width: 48,
    height: 48,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ rotate: '-6deg' }],
  },
  threeDPill: {
    position: 'absolute',
    top: 7,
    right: 7,
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 4,
    borderWidth: 1,
  },

  captionRow: {
    marginTop: 14,
    paddingHorizontal: 2,
  },
});
